import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { Version } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { endOfDay, endOfYear, isBefore, startOfYear } from 'date-fns'
import { useState } from 'react'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { Paths } from '../../../../lib/constants/path'
import { useFilters } from '../../../../lib/hooks/use-filters'
import useTranslate from '../../../../lib/hooks/use-translate'
import { currencyEnumMapping } from '../../../../lib/types/enums/common/currency.enum'
import { MissionStaffStatusEnum } from '../../../../lib/types/enums/mission/mission-staff-status.enum'
import { tripStepTypeMapping } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import {
  MissionToStaffFilter,
  MissionToStaffStandard
} from '../../../../lib/types/models/mission-to-staff/mission-to-staff'
import DynamicDataTable from '../../../elements/dynamic-table'
import MissionToStaffReportBadge from '../../mission-trackings/mission-to-staff-report-badge'

type Props = {
  staffId?: number
}

const initialFilter: MissionToStaffFilter = {
  statuses: [
    MissionStaffStatusEnum.PRE_AFFECTED,
    MissionStaffStatusEnum.AFFECTED,
    MissionStaffStatusEnum.PAID
  ],
  startDateFrom: DateFormatter.toDateFormatted(startOfYear(new Date())),
  startDateTo: DateFormatter.toDateFormatted(endOfYear(new Date()))
}

const extendedFilter: MissionToStaffFilter = {
  statuses: [
    MissionStaffStatusEnum.CANCELLED,
    MissionStaffStatusEnum.PRE_AFFECTED,
    MissionStaffStatusEnum.AFFECTED,
    MissionStaffStatusEnum.PAID
  ]
}

const StaffMissionsSection: React.FC<Props> = ({ staffId }) => {
  const { t } = useTranslate()
  const [mode, setMode] = useState<'initial' | 'extended'>('initial')

  const { selectedFilter, onSelectedFilterChange } = useFilters<MissionToStaffFilter>({
    initialFilters: initialFilter,
    options: { allowSearchParam: false }
  })

  const columns: Column<MissionToStaffStandard>[] = [
    {
      accessorKey: 'mission.directionality',
      header: 'Type de trajet',
      enableSorting: true,
      cell: ({ row }) => {
        const directionality = row.original.mission.directionality
        const passed = isBefore(endOfDay(new Date(row.original.mission.date)), new Date())
        const cancelled = row.original.status === MissionStaffStatusEnum.CANCELLED
        return (
          <div className="flex flex-col gap-1 py-1">
            <Badge
              variant={tripStepTypeMapping[directionality].variant}
              text={t(tripStepTypeMapping[directionality].label)}
            />
            {passed && !cancelled && <Badge variant="success" text="Passé" />}
            {cancelled && <Badge variant="danger" text="Annulé" />}
          </div>
        )
      }
    },
    {
      accessorKey: 'mission.date',
      header: 'Date',
      enableSorting: true,
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.mission.date)} />
    },
    {
      accessorKey: 'mission.title',
      header: 'Libellé',
      enableSorting: true
    },
    {
      accessorKey: 'pay',
      header: 'Rémunération',
      cell: ({ row }) => `${row.original.pay ?? 0} €`
    },
    {
      accessorKey: 'travelExpenses.validatedAmount',
      header: 'Frais',
      cell: ({ row }) => {
        const total = row.original.travelExpenses
          .map((expense) => expense.validatedAmount)
          .reduce((a, b) => (a ?? 0) + (b ?? 0), 0)
        return `${total} ${currencyEnumMapping[row.original.travelExpensesCurrency].symbol}`
      }
    },
    {
      accessorKey: 'rating',
      header: 'Note',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.rating !== undefined && row.original.rating !== null
          ? row.original.rating
          : '-'
    },
    {
      accessorKey: 'reportStatus',
      header: 'Rapport',
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MissionToStaffReportBadge
            missionToStaffId={row.original.id}
            reportStatus={row.original.reportStatus}
          />
        </div>
      )
    }
  ]

  if (!staffId) return <div>Aucun mission</div>
  return (
    <div>
      <Button
        label={mode === 'initial' ? 'Voir tous les missions' : 'Masquer les missions'}
        size="medium"
        type="button"
        variant={mode === 'initial' ? 'primary' : 'ghost'}
        onClick={() => {
          if (mode === 'initial') {
            onSelectedFilterChange(extendedFilter)
            setMode('extended')
          } else {
            onSelectedFilterChange(initialFilter)
            setMode('initial')
          }
        }}
      />
      <div className="h-4" />
      <DynamicDataTable<MissionToStaffStandard, MissionToStaffFilter>
        columns={columns}
        onRowClick={(data) => {
          if (!data.missionTracking) return
          window.open(
            Paths.MISSION_TRACKING_UPDATE_PAGE.replace(':id', data.missionTracking.id.toString()),
            '_blank',
            'noopener,noreferrer'
          )
        }}
        specialRows={[
          {
            condition: (row) => {
              const date = row.mission.date
              if (!date) return false
              return (
                isBefore(endOfDay(new Date(date)), new Date()) &&
                row.status !== MissionStaffStatusEnum.CANCELLED
              )
            },
            color: tailwindColors.green.light.hover
          },
          {
            condition: (row) => row.status === MissionStaffStatusEnum.CANCELLED,
            color: tailwindColors.red.light.hover
          }
        ]}
        endpoint={constructApiUrl(API.staffs.getStaffMissions.url, { id: staffId })}
        version={'STANDARD' as Version}
        headerType="none"
        filters={selectedFilter}
        paginated={false}
      />
    </div>
  )
}

export default StaffMissionsSection
